import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FormModal } from 'pages/global/FormModal';
import HelperFunctions from 'pages/global/HelperFunctions';
import { useContext, useRef, useState } from 'react';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { useGetLabelFolders } from 'pages/labels/hooks/useGetLabelFolders';
import Constants from 'config/Constants';
import { DocRevSelect, InputMultipleSelect } from 'pages/publications_v2/helpers/FieldHelper';
import { useGetLabels } from 'pages/labels/hooks/useGetLabels';
import { idToUri } from 'pages/global/UriHelper';
import { WarningButton } from 'components/Buttons';

const defaultContent = {
    labelFolder: '',
    options: [],
};

export function EditLabelBlockModal({ block, handleClose, index }) {
    const { t } = useTranslation('documents');
    const { updateBlock } = useContext(EditAreaContext);
    const folders = useGetLabelFolders();

    const latestContent = HelperFunctions.tryParseJSON(block.latestContent, defaultContent);

    const [labelFolder, setLabelFolder] = useState(latestContent.labelFolder);
    const [options = [], setOptions] = useState(latestContent.options);

    // Filter folders
    const allowedTypes = Object.keys(Constants.labelFolderEntities).filter((_entity) => {
        return Constants.labelFolderEntities[_entity].includes('labelBlock');
    });

    const availableFolders = folders.filter((_folder) =>
        _folder.contentTypes.some((_type) => allowedTypes.includes(_type)),
    );

    return (
        <FormModal
            show={true}
            onHide={handleClose}
            title={t('document.navbar.main.editor.left.blocks.tooltips.editLabelBlock')}
        >
            <>
                <Modal.Body>
                    <>
                        <Form.Group as={Row}>
                            <Form.Label column sm={4} htmlFor="labelFolder">
                                {t('document.navbar.main.editor.left.blocks.types.labelBlock.map')}
                            </Form.Label>
                            <Col sm={8}>
                                <DocRevSelect
                                    name="labelFolder"
                                    label={t('document.navbar.main.editor.left.blocks.types.labelBlock.map')}
                                    selectedValue={labelFolder}
                                    options={HelperFunctions.prepareDropdownData(availableFolders)}
                                    onChange={(selectedValue) => {
                                        setLabelFolder(selectedValue.value);
                                    }}
                                />
                            </Col>
                        </Form.Group>

                        {labelFolder !== '' && (
                            <LabelBlockOptions
                                block={block}
                                options={options}
                                setOptions={setOptions}
                                labelFolder={labelFolder}
                            />
                        )}
                    </>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="btn btn-secondary" onClick={() => handleClose()}>
                        {t('btn.close')}
                    </Button>
                    <WarningButton
                        disabled={
                            labelFolder === latestContent.labelFolder &&
                            JSON.stringify(options) === JSON.stringify(latestContent.options)
                        }
                        onClick={handleSubmit}
                    >
                        {t('global:btn.save')}
                    </WarningButton>
                </Modal.Footer>
            </>
        </FormModal>
    );

    function handleSubmit() {
        updateBlock(
            JSON.stringify({
                labelFolder,
                options,
            }),
            index,
            'latestContent',
        );

        handleClose();
    }
}

function LabelBlockOptions({ block, options = [], setOptions, labelFolder }) {
    const { t } = useTranslation('documents');
    const selectInputRef = useRef();
    const allLabels = useGetLabels();

    const labels = allLabels ? allLabels.filter((label) => label.folder === idToUri(labelFolder, 'LabelFolder')) : [];
    const { multiple, allow_add } = block.properties;

    return (
        <Form.Group as={Row}>
            <Form.Label column sm={4} htmlFor="options">
                {t('document.navbar.main.editor.left.blocks.types.labelBlock.value')}
            </Form.Label>
            <Col sm={8}>
                {multiple ? (
                    <div
                        onPaste={(event) => {
                            const pastedText = event.nativeEvent.clipboardData.getData('text');
                            const items = pastedText
                                .split(',')
                                .map((_item) => _item.trim())
                                .filter((_item) => options.find((_option) => _option.label === _item) === undefined);

                            if (items.length > 0) {
                                setOptions(
                                    options.concat(
                                        items
                                            .filter((_item) => {
                                                return (
                                                    allow_add ||
                                                    labels.find((_label) => _label.name === _item) !== undefined
                                                );
                                            })
                                            .map((_item) => {
                                                const existingLabel = labels.find((_label) => _label.name === _item);

                                                return {
                                                    id: existingLabel?.id ?? undefined,
                                                    value: existingLabel?.id ?? _item,
                                                    label: existingLabel?.name ?? _item,
                                                };
                                            }),
                                    ),
                                );

                                selectInputRef.current.select.blur();
                            }
                        }}
                    >
                        <InputMultipleSelect
                            props={{
                                ref: selectInputRef,
                            }}
                            name="options"
                            value={options}
                            defaultValue={options}
                            creatable={allow_add}
                            options={HelperFunctions.prepareDropdownData(labels)}
                            onChange={(selection) => {
                                const selectedValues = selection.map((_selection) => ({
                                    id: _selection.id,
                                    value: _selection.value,
                                    label: _selection.label,
                                }));

                                setOptions(selectedValues);
                            }}
                        />
                    </div>
                ) : (
                    <DocRevSelect
                        name="options"
                        options={HelperFunctions.prepareDropdownData(labels)}
                        selectedValue={options.length === 1 ? options[0]['value'] : ''}
                        onChange={(selection) => {
                            setOptions([
                                {
                                    id: selection.id,
                                    value: selection.value,
                                    label: selection.label,
                                },
                            ]);
                        }}
                    />
                )}
            </Col>
        </Form.Group>
    );
}
